/**
 * disc-pdp.js — interactive behaviors for the disc product page redesign.
 *
 * Currently only the exclusive-open accordion with auto-scroll-into-view.
 * Implemented as a Custom Element so multiple disc accordions on a page
 * (rare but possible — e.g. quick-buy popovers) work independently.
 *
 * On open: closes all sibling items, opens the clicked one, then smooth-scrolls
 * so the opened header sits 16px below the viewport top.
 *
 * On open of already-open: closes it (all-closed state OK).
 */

class DiscAccordion extends HTMLElement {
  static SCROLL_OFFSET = 16;

  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    this.addEventListener('click', this._onClick);

    // Open the default-open item on mount.
    const defaultKey = this.getAttribute('data-default-open');
    if (defaultKey) {
      const item = this.querySelector(`.disc-acc-item[data-acc-key="${defaultKey}"]`);
      if (item) this._open(item, /* scroll */ false);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick = (event) => {
    const header = event.target.closest('.disc-acc-header');
    if (!header) return;
    const item = header.closest('.disc-acc-item');
    if (!item) return;
    event.preventDefault();

    if (item.classList.contains('is-open')) {
      this._close(item);
    } else {
      this._open(item, /* scroll */ true);
    }
  };

  _open(item, doScroll) {
    // Close all others
    this.querySelectorAll('.disc-acc-item.is-open').forEach((other) => {
      if (other !== item) this._close(other);
    });

    item.classList.add('is-open');
    const header = item.querySelector('.disc-acc-header');
    const body = item.querySelector('.disc-acc-body');
    if (header) header.setAttribute('aria-expanded', 'true');
    if (body) body.hidden = false;

    if (doScroll) {
      // Wait for the height transition to start (one frame + brief delay),
      // then scroll the header just below the viewport top.
      requestAnimationFrame(() => {
        setTimeout(() => {
          const rect = item.getBoundingClientRect();
          const targetY = window.scrollY + rect.top - DiscAccordion.SCROLL_OFFSET;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }, 80);
      });
    }
  }

  _close(item) {
    item.classList.remove('is-open');
    const header = item.querySelector('.disc-acc-header');
    const body = item.querySelector('.disc-acc-body');
    if (header) header.setAttribute('aria-expanded', 'false');
    if (body) body.hidden = true;
  }
}

if (!customElements.get('disc-accordion')) {
  customElements.define('disc-accordion', DiscAccordion);
}

/**
 * <disc-tip> — click-to-toggle (or hover on desktop with mouse) tooltip.
 * Children: <button.disc-tip__btn> + <div.disc-tip__bubble>
 *
 * - Click trigger → opens. Click outside or ESC → closes.
 * - Only one tip open at a time (closes siblings on open).
 * - On desktop with mouse, hover also reveals (handled in CSS via :hover).
 */

class DiscTip extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.btn = this.querySelector('.disc-tip__btn');
    this.bubble = this.querySelector('.disc-tip__bubble');
    if (!this.btn || !this.bubble) return;

    this.btn.addEventListener('click', this._onTrigger);
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    this.btn?.removeEventListener('click', this._onTrigger);
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }

  _onTrigger = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const wasOpen = this.classList.contains('is-open');
    DiscTip.closeAll();
    if (!wasOpen) this._open();
  };

  _onDocClick = (event) => {
    if (!this.classList.contains('is-open')) return;
    if (!this.contains(event.target)) this._close();
  };

  _onKey = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this._close();
      this.btn?.focus();
    }
  };

  _open() {
    this.classList.add('is-open');
    this.btn.setAttribute('aria-expanded', 'true');
    this.bubble.hidden = false;
    // Position with JS — `position: fixed` so we're not constrained by any parent
    requestAnimationFrame(() => this._position());
    // Reposition on scroll/resize while open
    this._scrollHandler = () => this._position();
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    window.addEventListener('resize', this._scrollHandler, { passive: true });
  }

  _close() {
    this.classList.remove('is-open');
    this.btn?.setAttribute('aria-expanded', 'false');
    if (this.bubble) {
      this.bubble.hidden = true;
      this.bubble.removeAttribute('style');
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      window.removeEventListener('resize', this._scrollHandler);
      this._scrollHandler = null;
    }
  }

  _position() {
    if (!this.bubble || !this.btn) return;
    const vw = window.innerWidth;
    const margin = 16;
    const offset = 8;

    // Below 1000px → bottom-sheet style (works on phones + tablets).
    // Above → small positioned popover near the trigger.
    if (vw < 1000) {
      Object.assign(this.bubble.style, {
        position: 'fixed',
        left: '12px',
        right: '12px',
        bottom: '16px',
        top: 'auto',
        width: 'auto',
        maxWidth: 'none',
        transform: 'none',
      });
      return;
    }

    const btnRect = this.btn.getBoundingClientRect();
    const bubbleWidth = 280;
    const top = btnRect.bottom + offset;

    // Default: bubble's RIGHT aligns with the button's right (extends LEFT)
    const rightAlignedLeftEdge = btnRect.right - bubbleWidth;
    if (rightAlignedLeftEdge >= margin) {
      Object.assign(this.bubble.style, {
        position: 'fixed',
        top: `${top}px`,
        right: `${vw - btnRect.right}px`,
        left: 'auto',
        bottom: 'auto',
        width: `${bubbleWidth}px`,
        maxWidth: `calc(100vw - ${margin * 2}px)`,
        transform: 'none',
      });
    } else {
      // Fallback: left-align, clamp to viewport
      let left = btnRect.left;
      if (left + bubbleWidth > vw - margin) {
        left = vw - bubbleWidth - margin;
      }
      if (left < margin) left = margin;
      Object.assign(this.bubble.style, {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        right: 'auto',
        bottom: 'auto',
        width: `${bubbleWidth}px`,
        maxWidth: `calc(100vw - ${margin * 2}px)`,
        transform: 'none',
      });
    }
  }

  static closeAll() {
    document.querySelectorAll('disc-tip.is-open').forEach((el) => {
      el.classList.remove('is-open');
      const btn = el.querySelector('.disc-tip__btn');
      const bubble = el.querySelector('.disc-tip__bubble');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (bubble) bubble.hidden = true;
    });
  }
}

if (!customElements.get('disc-tip')) {
  customElements.define('disc-tip', DiscTip);
}

/**
 * Color swatch injection — finds color variant options in the disc PDP variant picker
 * and prepends a colored circle based on the Latvian color name.
 *
 * Runs on initial page load + on variant picker re-renders (Shopify replaces DOM
 * after variant change).
 */
const DISC_COLOR_MAP = {
  'sarkans': '#e63838',
  'oranžs': '#ff8c1a', 'oranzs': '#ff8c1a',
  'dzeltens': '#f9d423',
  'zaļš': '#2ecc71', 'zals': '#2ecc71',
  'zilganzaļš': '#16a085', 'zilganzals': '#16a085',
  'tirkīzs': '#1abc9c', 'tirkizs': '#1abc9c',
  'zils': '#2c5fa8',
  'violets': '#8e44ad',
  'rozā': '#ff5fb8', 'roza': '#ff5fb8',
  'gaiši rozā': '#ffa6cf', 'gaisi roza': '#ffa6cf',
  'pelēks': '#9aa0a6', 'peleks': '#9aa0a6',
  'tumši pelēks': '#4a4f55', 'tumsi peleks': '#4a4f55',
  'melns': '#1a1a1a',
  'balts': '#f5f5f5',
};

function discColorOne(name) {
  if (!name) return '#ccc';
  return DISC_COLOR_MAP[String(name).toLowerCase().trim()] || '#ccc';
}

function applyDiscSwatchBackground(dot, name) {
  if (!name) {
    dot.style.background = '#ccc';
    return;
  }
  const lower = String(name).toLowerCase().trim();
  // Special multi-color / effect names first
  if (lower.includes('dažādas') || lower.includes('dazadas')) {
    dot.style.background =
      'conic-gradient(from 0deg, #e63838, #f9d423, #2ecc71, #2c5fa8, #8e44ad, #e63838)';
    return;
  }
  if (lower === 'glow' || lower.includes('moonshine')) {
    dot.style.background = 'radial-gradient(circle at 30% 30%, #e8ffd0, #b8e986)';
    return;
  }
  if (lower === 'caurspīdīgs' || lower === 'caurspidigs') {
    dot.style.background = 'linear-gradient(135deg, #f5f5f5, #e0e0e0)';
    dot.style.borderColor = 'rgba(0,0,0,0.2)';
    return;
  }
  if (lower.includes('/')) {
    // Bi-color: split circle, 50% each
    const parts = lower.split('/').map((s) => s.trim()).filter(Boolean);
    const c1 = discColorOne(parts[0]);
    const c2 = discColorOne(parts[1] || parts[0]);
    dot.style.background = `linear-gradient(135deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`;
  } else {
    dot.style.background = discColorOne(lower);
  }
  // White or near-white needs a darker border to be visible
  if (
    lower === 'balts' || lower.startsWith('balts/') ||
    lower.includes('gaiši') || lower.includes('gaisi')
  ) {
    dot.style.borderColor = 'rgba(0,0,0,0.2)';
  }
}

function injectDiscColorSwatches(root = document) {
  // Only inside disc-pdp scope
  const pdp = root.closest ? root.closest('.disc-pdp') : null;
  const container = pdp || root.querySelector?.('.disc-pdp');
  if (!container) return;

  container.querySelectorAll('.variant-picker__option').forEach((picker) => {
    const legend = picker.querySelector('legend');
    if (!legend) return;
    const label = legend.textContent.toLowerCase();
    const isColor = label.includes('krās') || label.includes('color');
    if (!isColor) return;

    // Narrow to the actual chip container — `.variant-picker__option-values` holds the
    // clickable chip labels. Avoids matching outer labels (the legend's selected-value
    // span or app-injected labels) which produced stray dots outside the picker.
    const valuesContainer = picker.querySelector('.variant-picker__option-values');
    if (!valuesContainer) return;

    valuesContainer.querySelectorAll('label').forEach((opt) => {
      if (opt.dataset.discSwatchInjected) return;
      // The radio input may also be a child; we only want labels that wrap a value
      const valueText =
        opt.textContent.replace(/\s+/g, ' ').trim().split('\n')[0].trim();
      if (!valueText) return;
      const dot = document.createElement('span');
      dot.className = 'disc-injected-swatch';
      dot.setAttribute('aria-hidden', 'true');
      applyDiscSwatchBackground(dot, valueText);
      // Insert before any existing text content
      opt.insertBefore(dot, opt.firstChild);
      opt.dataset.discSwatchInjected = '1';
    });
  });
}

// Initial injection
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => injectDiscColorSwatches());
} else {
  injectDiscColorSwatches();
}

// Re-inject on variant change (Shopify replaces variant picker DOM)
document.addEventListener('variant:change', () => {
  setTimeout(() => injectDiscColorSwatches(), 50);
});

// Catch-all: observe disc-pdp for variant picker re-renders
(function watchDiscPdp() {
  const pdp = document.querySelector('.disc-pdp');
  if (!pdp) return;
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes.length > 0) {
        injectDiscColorSwatches();
        break;
      }
    }
  });
  observer.observe(pdp, { childList: true, subtree: true });
})();
